import { useState, useRef, useCallback, useEffect } from "react";
import SignatureCanvas from "react-signature-canvas";
import { jsPDF } from "jspdf";
import { X, Pen, Upload, Trash2, Check } from "lucide-react";
import { Button } from "./ui/button";

// Mechximize Director Signature - Local file (served from public folder)
const MECHXIMIZE_SIGNATURE_URL = "/mechximize-signature.png";

const ENDAModal = ({ 
  isOpen, 
  onClose, 
  formData, 
  orderItems, 
  leadTime,
  onSignAndSubmit 
}) => {
  const [signatureMode, setSignatureMode] = useState("draw"); // "draw" or "upload"
  const [uploadedSignature, setUploadedSignature] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [mechximizeSignatureBase64, setMechximizeSignatureBase64] = useState(null);
  const sigCanvasRef = useRef(null);
  const ndaContainerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load Mechximize signature as Base64 on mount
  useEffect(() => {
    const loadSignature = async () => {
      try {
        const response = await fetch(MECHXIMIZE_SIGNATURE_URL);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          setMechximizeSignatureBase64(reader.result);
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error('Failed to load Mechximize signature:', error);
      }
    };
    loadSignature();
  }, []);

  // Get current date formatted
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Clear signature when switching modes
  const handleModeSwitch = (mode) => {
    if (mode === "draw" && uploadedSignature) {
      setUploadedSignature(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } else if (mode === "upload" && sigCanvasRef.current) {
      sigCanvasRef.current.clear();
    }
    setSignatureMode(mode);
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedSignature(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Clear drawn signature
  const clearSignature = () => {
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
    }
  };

  // Get signature as Base64
  const getSignatureBase64 = useCallback(() => {
    if (signatureMode === "draw" && sigCanvasRef.current) {
      if (sigCanvasRef.current.isEmpty()) {
        return null;
      }
      return sigCanvasRef.current.getTrimmedCanvas().toDataURL("image/png");
    } else if (signatureMode === "upload" && uploadedSignature) {
      return uploadedSignature;
    }
    return null;
  }, [signatureMode, uploadedSignature]);

  // Generate PDF and submit
  const handleSignAndSubmit = async () => {
    const signatureBase64 = getSignatureBase64();
    
    if (!signatureBase64) {
      alert("Please provide your signature before signing the document.");
      return;
    }

    setIsGenerating(true);

    try {
      // ── Build PDF with jsPDF directly (no html2canvas / DOM rendering) ──────
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const pageW = pdf.internal.pageSize.getWidth();   // 210 mm
      const pageH = pdf.internal.pageSize.getHeight();  // 297 mm
      const lm = 20;                                    // left margin
      const cw = pageW - 2 * lm;                        // 170 mm content width
      let y = 22;

      // pt → mm line-height helper
      const lh = (size, spacing = 1.45) => size * 0.352778 * spacing;

      // Write wrapped text; auto page-break; returns nothing (mutates y)
      const write = (text, size = 11, style = 'normal', indent = 0) => {
        pdf.setFontSize(size);
        pdf.setFont('helvetica', style);
        const lines = pdf.splitTextToSize(text, cw - indent);
        if (y + lines.length * lh(size) > pageH - 18) { pdf.addPage(); y = 22; }
        pdf.text(lines, lm + indent, y);
        y += lines.length * lh(size);
      };

      // ── Title ────────────────────────────────────────────────────────────────
      write('NON-DISCLOSURE AND CONFIDENTIALITY AGREEMENT', 14, 'bold');
      y += 8;

      // ── Parties ──────────────────────────────────────────────────────────────
      write(`This Non-Disclosure Agreement (the "Agreement") is entered into as of ${currentDate}, by and between:`);
      y += 4;
      write('Receiving Party: Mechximize Digitech Private Limited, operating the MAKE @ IITB initiative in collaboration with BETIC.');
      y += 2;
      write(`Disclosing Party: ${formData.name}, representing ${formData.teamName || 'N/A'}, ${formData.labName || 'N/A'}, ${formData.department || 'N/A'}.`);
      y += 8;

      // ── Section 1 ────────────────────────────────────────────────────────────
      write('1. Purpose', 11, 'bold');
      y += 2;
      write('The Disclosing Party wishes to share certain proprietary files and specifications with the Receiving Party for the sole purpose of evaluating, quoting, and manufacturing custom components (the "Purpose").');
      y += 6;

      // ── Section 2 ────────────────────────────────────────────────────────────
      write('2. Confidential Information', 11, 'bold');
      y += 2;
      write('"Confidential Information" includes, but is not limited to, all uploaded 3D CAD models (e.g., .STEP files), 2D engineering drawings (e.g., .PDF files), material specifications, post-processing instructions, and any related intellectual property or unreleased product details submitted through the MAKE @ IITB platform.');
      y += 6;

      // ── Section 3 ────────────────────────────────────────────────────────────
      write('3. Obligations of the Receiving Party', 11, 'bold');
      y += 2;
      write('Mechximize Digitech Private Limited agrees to:');
      y += 2;
      [
        'Use the Confidential Information solely for the Purpose stated above.',
        'Maintain the absolute security of the designs and protect them against unauthorized disclosure.',
        'Not reverse-engineer, distribute, publish, or reproduce the Confidential Information for any external commercial gain.',
        'Restrict access to this information strictly to those employees, certified vendors, and manufacturing partners who have a "need to know" to fulfill the manufacturing request, all of whom are bound by equivalent confidentiality obligations.',
      ].forEach(ob => { write(`\u2022  ${ob}`, 11, 'normal', 5); y += 1; });
      y += 5;

      // ── Section 4 ────────────────────────────────────────────────────────────
      write('4. Term', 11, 'bold');
      y += 2;
      write('This Agreement shall govern all communications between the parties for the submitted RFQ. The obligations of confidentiality shall survive for a period of three (3) years from the date of disclosure.');
      y += 14;

      // ── Signatures ───────────────────────────────────────────────────────────
      if (y + 48 > pageH - 15) { pdf.addPage(); y = 22; }

      const col1 = lm;
      const col2 = lm + cw / 2 + 4;
      const colW = cw / 2 - 6;
      const sigTopY = y;

      // Left: Mechximize
      pdf.setFontSize(10); pdf.setFont('helvetica', 'bold');
      pdf.text('For Mechximize Digitech Private Limited:', col1, sigTopY);
      if (mechximizeSignatureBase64) {
        try { pdf.addImage(mechximizeSignatureBase64, 'PNG', col1, sigTopY + 4, 42, 15); } catch (_) {}
      }
      const lineY = sigTopY + 21;
      pdf.setDrawColor(80); pdf.line(col1, lineY, col1 + colW, lineY);
      pdf.setFontSize(9); pdf.setFont('helvetica', 'bold');
      pdf.text('Authorized Signatory', col1, lineY + 4);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Director', col1, lineY + 8);

      // Right: Disclosing Party
      pdf.setFontSize(10); pdf.setFont('helvetica', 'bold');
      pdf.text('For Disclosing Party:', col2, sigTopY);
      if (signatureBase64) {
        try {
          const fmt = signatureBase64.startsWith('data:image/png') ? 'PNG' : 'JPEG';
          pdf.addImage(signatureBase64, fmt, col2, sigTopY + 4, 42, 15);
        } catch (_) {}
      }
      pdf.setDrawColor(80); pdf.line(col2, lineY, col2 + colW, lineY);
      pdf.setFontSize(9); pdf.setFont('helvetica', 'bold');
      pdf.text(formData.name || '', col2, lineY + 4);
      pdf.setFont('helvetica', 'normal');
      pdf.text(formData.teamName || formData.department || '', col2, lineY + 8);

      const pdfBlob = pdf.output('blob');

      // Call the parent submit handler with the PDF blob
      await onSignAndSubmit(pdfBlob, signatureBase64);

    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Error generating NDA document. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-[#0A0A0A] border border-[#262626] w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#262626]">
          <div>
            <h2 className="font-heading text-xl font-bold text-white">E-NDA Agreement</h2>
            <p className="text-xs text-gray-500 font-mono mt-1">Review and sign the Non-Disclosure Agreement</p>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-2"
            data-testid="enda-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* NDA Document Preview */}
          <div 
            ref={ndaContainerRef}
            className="bg-white text-gray-900 p-8 rounded mb-6 max-h-[400px] overflow-y-auto"
            style={{ fontFamily: "'Times New Roman', serif" }}
          >
            <h2 className="text-center text-lg font-bold mb-6">NON-DISCLOSURE AND CONFIDENTIALITY AGREEMENT</h2>
            
            <p className="mb-4">This Non-Disclosure Agreement (the "Agreement") is entered into as of <strong>{currentDate}</strong>, by and between:</p>
            
            <p className="mb-4"><strong>Receiving Party:</strong> Mechximize Digitech Private Limited, operating the MAKE @ IITB initiative in collaboration with BETIC.</p>
            
            <p className="mb-4">
              <strong>Disclosing Party:</strong>{" "}
              <span className="bg-amber-200 px-1">{formData.name || "[Your Name]"}</span>, representing{" "}
              <span className="bg-amber-200 px-1">{formData.teamName || "[Team Name]"}</span>,{" "}
              <span className="bg-amber-200 px-1">{formData.labName || "[Lab Name]"}</span>,{" "}
              <span className="bg-amber-200 px-1">{formData.department || "[Department]"}</span>.
            </p>

            <h3 className="font-bold mt-6 mb-2">1. Purpose</h3>
            <p className="mb-4">The Disclosing Party wishes to share certain proprietary files and specifications with the Receiving Party for the sole purpose of evaluating, quoting, and manufacturing custom components (the "Purpose").</p>

            <h3 className="font-bold mt-6 mb-2">2. Confidential Information</h3>
            <p className="mb-4">"Confidential Information" includes, but is not limited to, all uploaded 3D CAD models (e.g., .STEP files), 2D engineering drawings (e.g., .PDF files), material specifications, post-processing instructions, and any related intellectual property or unreleased product details submitted through the MAKE @ IITB platform.</p>

            <h3 className="font-bold mt-6 mb-2">3. Obligations of the Receiving Party</h3>
            <p className="mb-2">Mechximize Digitech Private Limited agrees to:</p>
            <ul className="list-disc ml-6 mb-4 space-y-1">
              <li>Use the Confidential Information solely for the Purpose stated above.</li>
              <li>Maintain the absolute security of the designs and protect them against unauthorized disclosure.</li>
              <li>Not reverse-engineer, distribute, publish, or reproduce the Confidential Information for any external commercial gain.</li>
              <li>Restrict access to this information strictly to those employees, certified vendors, and manufacturing partners who have a "need to know" to fulfill the manufacturing request, all of whom are bound by equivalent confidentiality obligations.</li>
            </ul>

            <h3 className="font-bold mt-6 mb-2">4. Term</h3>
            <p className="mb-4">This Agreement shall govern all communications between the parties for the submitted RFQ. The obligations of confidentiality shall survive for a period of three (3) years from the date of disclosure.</p>

            {/* Signature Section Preview */}
            <div className="mt-10 pt-6 border-t border-gray-300 flex justify-between">
              <div className="w-[45%]">
                <p className="font-bold mb-2 text-sm">For Mechximize Digitech Private Limited:</p>
                {mechximizeSignatureBase64 ? (
                  <img 
                    src={mechximizeSignatureBase64} 
                    alt="Mechximize Signature" 
                    className="h-12 mb-2"
                  />
                ) : (
                  <div className="h-12 mb-2 flex items-center">
                    <span className="text-gray-400 text-xs">Loading signature...</span>
                  </div>
                )}
                <div className="border-t border-gray-400 pt-1">
                  <p className="text-xs font-bold">Authorized Signatory</p>
                  <p className="text-xs">Director</p>
                </div>
              </div>
              <div className="w-[45%]">
                <p className="font-bold mb-2 text-sm">For Disclosing Party:</p>
                <div className="h-12 border-b border-gray-400 mb-2 flex items-end">
                  <span className="text-gray-400 text-xs italic">Your signature will appear here</span>
                </div>
                <div className="pt-1">
                  <p className="text-xs font-bold">{formData.name || "[Your Name]"}</p>
                  <p className="text-xs">{formData.teamName || formData.department || "[Organization]"}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Signature Section */}
          <div className="border border-[#262626] bg-[#050505] p-6">
            <h3 className="font-mono text-sm uppercase tracking-wider text-gray-400 mb-4">Your Signature</h3>
            
            {/* Signature Mode Tabs */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => handleModeSwitch("draw")}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-mono uppercase tracking-wider border transition-all ${
                  signatureMode === "draw"
                    ? "border-amber-500 bg-amber-500/10 text-amber-500"
                    : "border-[#262626] text-gray-500 hover:text-white hover:border-gray-600"
                }`}
                data-testid="signature-draw-tab"
              >
                <Pen className="w-4 h-4" />
                Draw Signature
              </button>
              <button
                onClick={() => handleModeSwitch("upload")}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-mono uppercase tracking-wider border transition-all ${
                  signatureMode === "upload"
                    ? "border-amber-500 bg-amber-500/10 text-amber-500"
                    : "border-[#262626] text-gray-500 hover:text-white hover:border-gray-600"
                }`}
                data-testid="signature-upload-tab"
              >
                <Upload className="w-4 h-4" />
                Upload Signature
              </button>
            </div>

            {/* Draw Signature Canvas */}
            {signatureMode === "draw" && (
              <div>
                <div className="bg-white rounded relative" style={{ height: '150px' }}>
                  <SignatureCanvas
                    ref={sigCanvasRef}
                    canvasProps={{
                      className: "w-full h-full rounded",
                      style: { width: '100%', height: '150px' }
                    }}
                    backgroundColor="white"
                    penColor="black"
                  />
                </div>
                <button
                  onClick={clearSignature}
                  className="mt-3 flex items-center gap-2 text-gray-400 hover:text-red-500 transition-colors text-sm font-mono"
                  data-testid="clear-signature-btn"
                >
                  <Trash2 className="w-4 h-4" />
                  Clear Signature
                </button>
              </div>
            )}

            {/* Upload Signature */}
            {signatureMode === "upload" && (
              <div>
                <label className="block">
                  <div className={`border-2 border-dashed rounded p-6 text-center cursor-pointer transition-colors ${
                    uploadedSignature 
                      ? "border-amber-500 bg-amber-500/5" 
                      : "border-[#262626] hover:border-gray-600"
                  }`}>
                    {uploadedSignature ? (
                      <div className="bg-white rounded p-4 inline-block">
                        <img 
                          src={uploadedSignature} 
                          alt="Uploaded Signature" 
                          className="max-h-20 mx-auto"
                        />
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                        <p className="text-gray-400 text-sm">Click to upload your signature</p>
                        <p className="text-gray-600 text-xs mt-1">PNG or JPEG, transparent background recommended</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png, image/jpeg"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="signature-file-input"
                  />
                </label>
                {uploadedSignature && (
                  <button
                    onClick={() => {
                      setUploadedSignature(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    className="mt-3 flex items-center gap-2 text-gray-400 hover:text-red-500 transition-colors text-sm font-mono"
                  >
                    <Trash2 className="w-4 h-4" />
                    Remove Signature
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-4 border-t border-[#262626] bg-[#050505]">
          <button
            onClick={onClose}
            className="px-6 py-3 text-gray-400 hover:text-white transition-colors font-mono text-sm uppercase tracking-wider"
            data-testid="enda-cancel-btn"
          >
            Cancel
          </button>
          <Button
            onClick={handleSignAndSubmit}
            disabled={isGenerating}
            className="btn-mechanical pulse-glow bg-amber-500 text-black hover:bg-amber-400 font-mono uppercase tracking-wider text-sm rounded-none px-8 py-4 disabled:opacity-50"
            data-testid="enda-sign-submit-btn"
          >
            {isGenerating ? (
              <>Generating Document...</>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                I Agree & Sign Document
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ENDAModal;
